import type { Attributes, CreateHeroRequest, Hero, HeroCreation, Inventory, Loadout, Slot } from '@mezha/api'
import { baseApi } from '../baseApi'

export const heroApi = baseApi.injectEndpoints({
  endpoints: b => ({
    hero: b.query<Hero, void>({ query: () => ({ get: '/api/v1/hero' }), providesTags: ['Hero'] }),
    creation: b.query<HeroCreation, void>({ query: () => ({ get: '/api/v1/hero/creation' }) }),
    createHero: b.mutation<Hero, Omit<CreateHeroRequest, 'request_id'>>({
      query: body => ({ command: '/api/v1/hero', body }),
      invalidatesTags: ['Me', 'World', 'Story'],
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled
        dispatch(heroApi.util.upsertQueryData('hero', undefined, data))
      },
    }),
    healInjury: b.mutation<Hero, { expected_version: number }>({
      query: body => ({ command: '/api/v1/hero/injury/heal', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled
        dispatch(heroApi.util.upsertQueryData('hero', undefined, data))
      },
    }),
    easeInjury: b.mutation<Hero, { expected_version: number }>({
      query: body => ({ command: '/api/v1/hero/injury/ease', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled
        dispatch(heroApi.util.upsertQueryData('hero', undefined, data))
      },
    }),
    allocate: b.mutation<Hero, { expected_version: number; add: Attributes }>({
      query: body => ({ command: '/api/v1/hero/attributes/allocate', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled
        dispatch(heroApi.util.upsertQueryData('hero', undefined, data))
        dispatch(baseApi.util.invalidateTags(['Loadout']))
      },
    }),
    resetAttributes: b.mutation<Hero, { expected_version: number }>({
      query: body => ({ command: '/api/v1/hero/attributes/reset', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled
        dispatch(heroApi.util.upsertQueryData('hero', undefined, data))
        dispatch(baseApi.util.invalidateTags(['Loadout']))
      },
    }),
    inventory: b.query<Inventory, void>({ query: () => ({ get: '/api/v1/inventory' }), providesTags: ['Inventory'] }),
    loadout: b.query<Loadout, void>({ query: () => ({ get: '/api/v1/loadout' }), providesTags: ['Loadout'] }),
    equip: b.mutation<Loadout, { expected_version: number; slot: Slot; entry_id: number }>({
      query: body => ({ command: '/api/v1/loadout/equip', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(heroApi.util.upsertQueryData('loadout', undefined, data))
          dispatch(heroApi.util.upsertQueryData('hero', undefined, data.hero))
          dispatch(baseApi.util.invalidateTags(['Inventory', 'Skills', 'World']))
        } catch {
          // A lost reply may follow a committed command. Reconcile instead
          // of guessing whether the item or its resource bonus was applied.
          dispatch(baseApi.util.invalidateTags(['Loadout', 'Hero', 'Inventory', 'Skills', 'World']))
        }
      },
    }),
    unequip: b.mutation<Loadout, { expected_version: number; slot: Slot }>({
      query: body => ({ command: '/api/v1/loadout/unequip', body }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(heroApi.util.upsertQueryData('loadout', undefined, data))
          dispatch(heroApi.util.upsertQueryData('hero', undefined, data.hero))
          dispatch(baseApi.util.invalidateTags(['Inventory', 'Skills', 'World']))
        } catch {
          dispatch(baseApi.util.invalidateTags(['Loadout', 'Hero', 'Inventory', 'Skills', 'World']))
        }
      },
    }),
  }),
})

export const {
  useHeroQuery, useCreationQuery, useCreateHeroMutation, useHealInjuryMutation, useEaseInjuryMutation, useAllocateMutation, useResetAttributesMutation,
  useInventoryQuery, useLoadoutQuery, useEquipMutation, useUnequipMutation,
} = heroApi
